import type { Y, YjsEditor } from '@tunasong/sync-lib'
import type { Operation } from 'slate'
import { Editor } from 'slate'

/**
 * Normalize remote changes once, when the task that applied them ends, not after every remote transaction
 * (GH #2085).
 *
 * slate-yjs normalizes at the end of each `applyRemoteEvents`. The normalizer ops are local changes that reach
 * the Y.Doc only on the next onChange, and the next `applyRemoteEvents` flushes them first. The websocket
 * provider applies every frame of a message back to back, in one task, so a frame that arrives while those ops
 * are pending gets them flushed by position against a Y tree it has already changed. When a peer's frame holds
 * the same repair (both clients normalized the same state), the stale merge throws "Cannot merge y text with y
 * element", and Slate and the Y.Doc drift apart.
 *
 * Holding normalization until Slate's onChange keeps the Slate tree identical to the Y tree while frames are
 * applied. The normalizers then run once, on the state after every frame, and their ops are flushed by the same
 * onChange. A repair the sender already made has nothing left to do.
 *
 * Remote events applied while something else holds normalization (an undo, see `withNormalizeAfterUndo`) are
 * left to that owner.
 */
export const withNormalizeRemoteOncePerTask = <T extends YjsEditor>(editor: T) => {
  const { applyRemoteEvents, onChange } = editor
  let deferred = false

  const normalizeDeferred = () => {
    if (!deferred) {
      return
    }
    deferred = false
    Editor.setNormalizing(editor, true)
    Editor.normalize(editor)
  }

  editor.applyRemoteEvents = (events: Y.YEvent<Y.XmlText>[], origin: unknown) => {
    if (!Editor.isNormalizing(editor)) {
      applyRemoteEvents(events, origin)
      return
    }
    deferred = true
    Editor.setNormalizing(editor, false)
    const operationCount = editor.operations.length
    try {
      applyRemoteEvents(events, origin)
    } finally {
      // No Slate op means Slate scheduled no onChange: normalize now rather than never.
      if (editor.operations.length === operationCount) {
        normalizeDeferred()
      }
    }
  }

  editor.onChange = (options?: { operation?: Operation }) => {
    normalizeDeferred()
    onChange(options)
  }

  return editor
}
